import { type CrossBrandMallHomeCampaign } from '@hooks-api/types/dataModel/cross-brand-mall'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowRightSmall } from '@pets-ui/Icons'
import { Link } from '@pets-ui/Link/Link'
import { Paper } from '@pets-ui/Paper/Paper'
import { Text } from '@pets-ui/Text/Text'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useHistory } from 'react-router-dom-v5'
import Lightning from '../../assets/lightning-icon.svg?react'
import { useEligibleBrandSelection } from '../hooks/use-eligible-brand-selection'
import { useHomeAuthorizedCampaigns } from '../hooks/use-home-authorized-campaigns'
import { BrandNewArrivalAvailabilityChecker } from './BrandNewArrivalAvailabilityChecker'
import { BrandNewArrivalSectionContent, BrandNewArrivalSectionContentSkeleton } from './BrandNewArrivalSectionContent'
import { BrandTabList } from './BrandTabList'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  contentRow: {
    flexDirection: { [MOBILE]: 'column', default: 'row' },
    flexWrap: 'nowrap',
  },
  tabSidebar: {
    borderColor: colors.grey200,
    borderStyle: 'solid',
    flexShrink: 0,
    borderBottomWidth: { [MOBILE]: 1, default: 0 },
    borderRightWidth: { [MOBILE]: 0, default: 1 },
    width: { [MOBILE]: '100%', default: 120 },
  },
  headerBadgeDesktop: {
    display: { [MOBILE]: 'none', default: 'block' },
  },
})

interface BrandNewArrivalSectionProps {
  campaigns: CrossBrandMallHomeCampaign[]
  currency: string
  isDetailLoading: boolean
  basePath: string
}

interface BrandNewArrivalSectionInnerProps {
  campaigns: CrossBrandMallHomeCampaign[]
  currency: string
  basePath: string
}

interface BrandNewArrivalSectionHeaderProps {
  basePath: string
}

const BrandNewArrivalSectionHeader = ({ basePath }: BrandNewArrivalSectionHeaderProps) => {
  const history = useHistory()
  const handleNavigateToNewProducts = () => {
    history.push(`${basePath}/new-products`)
  }
  return (
    <HStack
      p={4}
      alignItems="center"
      justifyContent="space-between"
      backgroundColor={colors.white}
      borderBottomWidth={1}
      borderBottomStyle="solid"
      borderBottomColor={colors.grey200}
    >
      <HStack gap={2} alignItems="center">
        <Lightning />
        <Text variant="h5" bold color={colors.grey900}>新品上市</Text>
        <Box rootStyle={styles.headerBadgeDesktop} borderColor={colors.red600} borderWidth={1} borderTopLeftRadius={8} borderTopRightRadius={8} borderBottomRightRadius={8} px={2}>
          <Text color={colors.red600}>搶先同行掌握紅利</Text>
        </Box>
      </HStack>
      <Link onClick={handleNavigateToNewProducts}>
        <HStack gap={1} alignItems="center">
          前往新品專區
          <ArrowRightSmall fontSize={16} color={colors.blue600} />
        </HStack>
      </Link>
    </HStack>
  )
}

const BrandNewArrivalSectionSkeleton = () => (
  <HStack rootStyle={styles.contentRow}>
    <Box rootStyle={styles.tabSidebar}>
      <BrandTabList
        campaigns={[]}
        activeCampaignId={null}
        onChange={() => undefined}
        isLoading={true}
      />
    </Box>
    <BrandNewArrivalSectionContentSkeleton />
  </HStack>
)

const BrandNewArrivalSectionInner = (props: BrandNewArrivalSectionInnerProps) => {
  const { campaigns, currency, basePath } = props

  const authorizedCampaigns = useHomeAuthorizedCampaigns(campaigns)

  const {
    orderedCampaigns,
    eligibleCampaigns,
    selectedCampaign: activeCampaign,
    isAvailabilityResolved,
    setAvailability,
    setSelectedCampaignId,
  } = useEligibleBrandSelection(authorizedCampaigns)

  if (orderedCampaigns.length === 0) return null

  if (isAvailabilityResolved && eligibleCampaigns.length === 0) return null

  return (
    <Paper borderRadius={8} elevation={0} overflow="hidden">
      <VStack>
        <BrandNewArrivalSectionHeader basePath={basePath} />
        {orderedCampaigns.map(campaign => (
          <BrandNewArrivalAvailabilityChecker key={campaign.campaign_id} campaign={campaign} onAvailabilityChange={setAvailability} />
        ))}
        {!isAvailabilityResolved
          ? <BrandNewArrivalSectionSkeleton />
          : (
            <HStack rootStyle={styles.contentRow}>
              <Box rootStyle={styles.tabSidebar}>
                <BrandTabList
                  campaigns={eligibleCampaigns}
                  activeCampaignId={activeCampaign?.campaign_id ?? null}
                  onChange={setSelectedCampaignId}
                  isLoading={false}
                />
              </Box>
              {activeCampaign && (
                <BrandNewArrivalSectionContent
                  key={activeCampaign.campaign_id}
                  campaign={activeCampaign}
                  currency={currency}
                  basePath={basePath}
                />
              )}
            </HStack>
          )}
      </VStack>
    </Paper>
  )
}

export const BrandNewArrivalSection = (props: BrandNewArrivalSectionProps) => {
  const { campaigns, currency, isDetailLoading, basePath } = props

  if (isDetailLoading) {
    return (
      <Paper borderRadius={8} elevation={0} overflow="hidden">
        <VStack>
          <BrandNewArrivalSectionHeader basePath={basePath} />
          <BrandNewArrivalSectionSkeleton />
        </VStack>
      </Paper>
    )
  }

  return (
    <BrandNewArrivalSectionInner
      campaigns={campaigns}
      currency={currency}
      basePath={basePath}
    />
  )
}


